import { createContext, useContext, useState, useEffect, useCallback, useRef, type ReactNode } from 'react';

const STORAGE_KEY = 'xelacore-quick-access-orgs';
const DEBOUNCE_MS = 500;

export interface QuickAccessOrg {
  id: string;
  name: string;
  type: string;
  addedAt: number;
}

interface QuickAccessContextType {
  quickAccessOrgs: QuickAccessOrg[];
  addToQuickAccess: (org: { id: string; name: string; type: string }) => void;
  removeFromQuickAccess: (orgId: string) => void;
  toggleQuickAccess: (org: { id: string; name: string; type: string }) => void;
  updateQuickAccessOrg: (orgId: string, updates: Partial<Pick<QuickAccessOrg, 'name' | 'type'>>) => void;
  isInQuickAccess: (orgId: string) => boolean;
  clearQuickAccess: () => void;
  isInitialized: boolean;
}

const QuickAccessContext = createContext<QuickAccessContextType | null>(null);

export function QuickAccessProvider({ children }: { children: ReactNode }) {
  const [quickAccessOrgs, setQuickAccessOrgs] = useState<QuickAccessOrg[]>([]);
  const [isInitialized, setIsInitialized] = useState(false);

  // Ref to track debounce timeout
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  // Ref to track the latest state for saving (avoids stale closure)
  const latestOrgsRef = useRef<QuickAccessOrg[]>([]);

  // Keep ref in sync with state
  useEffect(() => {
    latestOrgsRef.current = quickAccessOrgs;
  }, [quickAccessOrgs]);

  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed)) {
          setQuickAccessOrgs(parsed);
          latestOrgsRef.current = parsed;
        }
      }
    } catch (e) {
      console.error('Failed to load quick access from localStorage:', e);
    }
    setIsInitialized(true);
  }, []);

  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
        // Save immediately on unmount if there are pending changes
        try {
          localStorage.setItem(STORAGE_KEY, JSON.stringify(latestOrgsRef.current));
        } catch (e) {
          console.error('Failed to save quick access on unmount:', e);
        }
      }
    };
  }, []);

  const debouncedSave = useCallback(() => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    saveTimeoutRef.current = setTimeout(() => {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(latestOrgsRef.current));
      } catch (e) {
        console.error('Failed to save quick access to localStorage:', e);
      }
      saveTimeoutRef.current = null;
    }, DEBOUNCE_MS);
  }, []);

  const addToQuickAccess = useCallback((org: { id: string; name: string; type: string }) => {
    setQuickAccessOrgs((prev) => {
      if (prev.some((o) => o.id === org.id)) {
        return prev;
      }
      return [...prev, { ...org, addedAt: Date.now() }];
    });
    debouncedSave();
  }, [debouncedSave]);

  const removeFromQuickAccess = useCallback((orgId: string) => {
    setQuickAccessOrgs((prev) => prev.filter((o) => o.id !== orgId));
    debouncedSave();
  }, [debouncedSave]);

  const updateQuickAccessOrg = useCallback(
    (orgId: string, updates: Partial<Pick<QuickAccessOrg, 'name' | 'type'>>) => {
      setQuickAccessOrgs((prev) => {
        let changed = false;
        const next = prev.map((o) => {
          if (o.id !== orgId) return o;
          const merged = { ...o, ...updates };
          if (merged.name !== o.name || merged.type !== o.type) {
            changed = true;
            return merged;
          }
          return o;
        });
        return changed ? next : prev;
      });
      debouncedSave();
    },
    [debouncedSave],
  );

  const toggleQuickAccess = useCallback((org: { id: string; name: string; type: string }) => {
    setQuickAccessOrgs((prev) => {
      const exists = prev.some((o) => o.id === org.id);
      if (exists) {
        return prev.filter((o) => o.id !== org.id);
      } else {
        return [...prev, { ...org, addedAt: Date.now() }];
      }
    });
    debouncedSave();
  }, [debouncedSave]);

  const isInQuickAccess = useCallback((orgId: string) => {
    return quickAccessOrgs.some((o) => o.id === orgId);
  }, [quickAccessOrgs]);

  const clearQuickAccess = useCallback(() => {
    setQuickAccessOrgs([]);
    debouncedSave();
  }, [debouncedSave]);

  return (
    <QuickAccessContext.Provider
      value={{
        quickAccessOrgs,
        addToQuickAccess,
        removeFromQuickAccess,
        toggleQuickAccess,
        updateQuickAccessOrg,
        isInQuickAccess,
        clearQuickAccess,
        isInitialized,
      }}
    >
      {children}
    </QuickAccessContext.Provider>
  );
}

export function useQuickAccess() {
  const context = useContext(QuickAccessContext);
  if (!context) {
    throw new Error('useQuickAccess must be used within a QuickAccessProvider');
  }
  return context;
}
